import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Plus } from 'lucide-react';
import { api } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { usePincodeAutofill } from '@/hooks/usePincodeAutofill';
import { useServiceabilityCheck } from '@/hooks/useServiceabilityCheck';
import { CustomerLookupCard } from '@/components/order/CustomerLookupCard';
import { OrderValidationPanel, ruleCheck, type ValidatableOrder } from './OrderValidationPanel';
import { QuickCreateMerchant, QuickCreateCourier, QuickCreatePickup } from './QuickCreateButtons';

interface Merchant { id: number; companyName: string }
interface Courier { id: number; name: string; orderType: 'B2C' | 'B2B' }
interface State { id: number; name: string }
interface Warehouse { id: number; name: string; merchantId?: number }

// Keystroke guards for the booking form's numeric fields.
// onlyDigitKeys → mobile / pincode / qty (whole numbers only, no letters or symbols).
// blockNonNumericKeys → invoice value / weight / dimensions (decimals allowed, but no e/E/+/-).
const onlyDigitKeys = (e: any) => { if (e.ctrlKey || e.metaKey || e.altKey) return; if (e.key.length === 1 && !/[0-9]/.test(e.key)) e.preventDefault(); };
const blockNonNumericKeys = (e: any) => { if (e.ctrlKey || e.metaKey || e.altKey) return; if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); };

interface FormData {
  lrGenerateType: '1' | '2';
  lrNo?: string;
  freightCollection: string;
  pickupType: string;
  insuranceType: 'OWNER_RISK' | 'CARRIER_RISK';
  merchantId: number | '';
  courierId?: number | '';
  warehouseId?: number | '';

  customerMobile: string;
  customerName: string;
  customerAddress: string;
  customerPincode: string;
  customerCity: string;
  customerState: string;

  productName?: string;
  projectName?: string;
  totalWeight?: number;
  isFragile?: 'Yes' | 'No' | '';

  subOrders: Array<{ description?: string; clientRefId?: string; qty?: number }>;
  dimensions: Array<{ qty?: number; length?: number; breadth?: number; height?: number; weight?: number }>;

  deliveryType: 'COD' | 'PREPAID';
  codValue?: number;
  invoiceDetails: Array<{ productInvoiceValue?: number; invoiceNumber?: string; ewayBillNumber?: string }>;
}

// Build create-form defaults from an existing order when "Duplicate Order" is used.
// Copies everything EXCEPT identifiers that must stay unique: the LR number and AWB
// number are never carried over (lrNo is cleared; lrGenerateType keeps the source mode
// so a Manual order still prompts for a fresh LR No).
function b2bDefaultsFromOrder(o: any): Partial<FormData> {
  const rawDim: any[] = Array.isArray(o?.dimensions) ? o.dimensions : [];
  const rawSub: any[] = Array.isArray(o?.subOrders) ? o.subOrders : [];
  const N = Math.max(rawSub.length, rawDim.length, 1);
  const merged = Array.from({ length: N }, (_, i) => {
    const s: any = rawSub[i] ?? {};
    const d: any = rawDim[i] ?? {};
    return {
      description: s.description ?? '',
      clientRefId: s.clientRefId ?? '',
      qty: Number(s.qty ?? d.qty ?? 1) || 1,
      length: Number(s.length ?? d.length ?? 0) || 0,
      breadth: Number(s.breadth ?? d.breadth ?? 0) || 0,
      height: Number(s.height ?? d.height ?? 0) || 0,
      weight: Number(s.weight ?? d.weight ?? 0) || 0,
    };
  });
  const subOrders = merged.map(m => ({ description: m.description, clientRefId: m.clientRefId, qty: m.qty }));
  const dimensions = merged.map(m => ({ qty: m.qty, length: m.length, breadth: m.breadth, height: m.height, weight: m.weight }));
  const prod0: any = Array.isArray(o?.products) && o.products.length ? o.products[0] : {};
  let invoiceDetails: any[] = [{}];
  try {
    const parsed = typeof o?.userRemarks === 'string' ? JSON.parse(o.userRemarks) : o?.userRemarks;
    if (Array.isArray(parsed?.invoiceDetails) && parsed.invoiceDetails.length) {
      invoiceDetails = parsed.invoiceDetails.map((iv: any) => ({
        productInvoiceValue: iv.productInvoiceValue ?? undefined,
        invoiceNumber: iv.invoiceNumber ?? '',
        ewayBillNumber: iv.ewayBillNumber ?? '',
      }));
    }
  } catch { /* userRemarks not JSON — leave a single empty invoice row */ }
  const lrGenerateType: '1' | '2' = (o?.lrGenerateType === '1' || o?.lrGenerateType === '2')
    ? o.lrGenerateType
    : (o?.lrNo ? '2' : '1');
  return {
    lrGenerateType,
    lrNo: '',
    freightCollection: o?.freightCollection ?? '',
    pickupType: o?.pickupType ?? '',
    insuranceType: o?.insuranceType === 'CARRIER_RISK' ? 'CARRIER_RISK' : 'OWNER_RISK',
    merchantId: o?.merchantId ?? '',
    courierId: o?.courierId ?? '',
    warehouseId: o?.warehouseId ?? '',
    customerMobile: o?.customerMobile ?? '',
    customerName: o?.customerName ?? '',
    customerAddress: o?.customerAddress ?? '',
    customerPincode: o?.customerPincode ?? '',
    customerCity: o?.customerCity ?? '',
    customerState: o?.customerState ?? '',
    productName: prod0?.name ?? '',
    projectName: prod0?.project ?? '',
    totalWeight: prod0?.weight != null ? Number(prod0.weight) : (o?.chargeableWeight != null ? Number(o.chargeableWeight) : undefined),
    isFragile: prod0?.isFragile === true ? 'Yes' : (prod0?.isFragile === false ? 'No' : ''),
    subOrders: subOrders.length ? subOrders : [{}],
    dimensions: dimensions.length ? dimensions : [{}],
    deliveryType: o?.deliveryType === 'COD' ? 'COD' : 'PREPAID',
    codValue: o?.deliveryType === 'COD' ? (Number(o?.codValue ?? 0) || undefined) : undefined,
    invoiceDetails,
  };
}

function normalizeB2b(d: any): ValidatableOrder {
  return {
    customerName: d.customerName, customerMobile: d.customerMobile, customerAddress: d.customerAddress,
    customerCity: d.customerCity, customerState: d.customerState, customerPincode: d.customerPincode,
    totalWeight: d.totalWeight != null ? Number(d.totalWeight) : undefined,
    dimensions: Array.isArray(d.dimensions)
      ? d.dimensions.map((x: any) => ({ length: x?.length, breadth: x?.breadth, height: x?.height, weight: x?.weight, qty: x?.qty }))
      : [],
  };
}

export function CreateOrderB2bPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const dup = (loc.state as any)?.duplicateFrom;
  // When arriving from the Orders Bucket "Move to B2B" action, `dup` is the bucket payload pre-fill and
  // bucketId lets us mark that bucket routed once the order is saved.
  const bucketId = (loc.state as any)?.bucketId as string | undefined;
  // Arriving from RTO Orders → Re-dispatch: the id of the RETURNED shipment this one replaces, and
  // the operator's reason. The form itself is prefilled through `duplicateFrom` like any duplicate.
  const redispatchFrom = (loc.state as any)?.redispatchFrom as string | undefined;
  const redispatchRemarks = (loc.state as any)?.redispatchRemarks as string | undefined;

  const { register, handleSubmit, control, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: dup ? b2bDefaultsFromOrder(dup) : {
      lrGenerateType: '2',
      insuranceType: 'OWNER_RISK',
      deliveryType: 'PREPAID',
      subOrders: [{}],
      dimensions: [{}],
      invoiceDetails: [{}],
    },
  });
  const customerPincode = watch('customerPincode');
  const customerMobile = watch('customerMobile');
  const watchCourierId = watch('courierId');
  const svcCheck = useServiceabilityCheck(watchCourierId, customerPincode);
  const notServiceable = !!svcCheck.data?.found && svcCheck.data?.serviceable === false;
  usePincodeAutofill(customerPincode, (d) => {
    setValue('customerCity', d.city);
    setValue('customerState', d.state);
  });

  const { fields: subOrders, append: addSub, remove: rmSub } = useFieldArray({ control, name: 'subOrders' });
  const { fields: dims, append: addDim, remove: rmDim } = useFieldArray({ control, name: 'dimensions' });
  const { fields: invs, append: addInv, remove: rmInv } = useFieldArray({ control, name: 'invoiceDetails' });

  // Merchant portal ships only as itself — auto-pick its own merchant id and hide the picker (admin-only).
  const { user } = useAuth();
  const isMerchant = user?.kind === 'merchant';
  const myMerchantId = isMerchant ? user?.id : undefined;
  useEffect(() => {
    if (isMerchant && myMerchantId) setValue('merchantId', myMerchantId as any, { shouldValidate: true });
  }, [isMerchant, myMerchantId, setValue]);

  const { data: merchants } = useQuery({ queryKey: ['merchants', 'B2B'], enabled: !isMerchant, queryFn: async () => (await api.get<Merchant[]>('/merchants', { params: { serviceType: 'B2B' } })).data });
  const { data: couriers } = useQuery({ queryKey: ['couriers', 'B2B-active'], queryFn: async () => (await api.get<Courier[]>('/couriers', { params: { orderType: 'B2B', isActive: 'true' } })).data });
  const { data: states } = useQuery({ queryKey: ['states'], queryFn: async () => (await api.get<State[]>('/states')).data });

  const selectedMerchant = watch('merchantId');
  const { data: assignedCouriers } = useQuery({
    queryKey: ['merchant-couriers', selectedMerchant],
    enabled: !!selectedMerchant,
    queryFn: async () => (await api.get<any[]>(`/merchants/${selectedMerchant}/couriers`, { params: { activeOnly: 1 } })).data,
  });
  // Filter to B2B or BOTH couriers, fall back to all if none assigned
  const courierOptions = (() => {
    const allowed = (assignedCouriers ?? []).map((mc: any) => mc.courier).filter((c: any) => !c.orderType || c.orderType === 'B2B' || c.orderType === 'BOTH');
    if (selectedMerchant && allowed.length > 0) return allowed;
    return couriers ?? [];
  })();
  const { data: warehouses } = useQuery({
    queryKey: ['merchant-warehouses', selectedMerchant],
    queryFn: async () => (await api.get<Warehouse[]>(`/merchants/${selectedMerchant}/warehouses`, { params: { includeMaster: 'true' } })).data,
    enabled: !!selectedMerchant,
  });

  const lrGenType = watch('lrGenerateType');

  const create = useMutation({
    mutationFn: async (d: FormData) => {
      // Block booking to a pincode the selected courier explicitly doesn't serve (backend re-checks too).
      if (d.courierId && svcCheck.data?.found && svcCheck.data?.serviceable === false) {
        throw new Error(`Destination pincode ${d.customerPincode} is not serviceable by the selected courier. Choose a different courier or pincode.`);
      }
      const invoiceValue = (d.invoiceDetails ?? []).reduce((s, i) => s + (Number(i.productInvoiceValue) || 0), 0);
      // Validation: zero is not allowed in sub-order qty + per-box dimensions
      for (const so of (d.subOrders ?? [])) {
        if (!so.qty || Number(so.qty) <= 0) throw new Error('Sub-order quantity must be > 0');
      }
      for (const dim of (d.dimensions ?? [])) {
        if (!dim.qty || Number(dim.qty) <= 0) throw new Error('Per-box dimensions: qty must be > 0');
        if (!dim.length || Number(dim.length) <= 0) throw new Error('Per-box dimensions: length must be > 0');
        if (!dim.breadth || Number(dim.breadth) <= 0) throw new Error('Per-box dimensions: breadth must be > 0');
        if (!dim.height || Number(dim.height) <= 0) throw new Error('Per-box dimensions: height must be > 0');
      }
      // Sub-orders and box dimensions are INDEPENDENT (do NOT merge them):
      //  - dimensions[] = physical boxes (each row = `qty` boxes of that L×B×H). B2B captures only
      //    dimensions + qty per box (no per-box weight).
      //  - subOrders[]  = line items (description / client ref / qty)
      // Physical box count = sum of dimension qtys.
      const dimRowsRaw = (d.dimensions ?? [])
        .filter(x => x.length || x.breadth || x.height)
        .map(x => ({ qty: Number(x.qty ?? 1) || 1, length: Number(x.length ?? 0), breadth: Number(x.breadth ?? 0), height: Number(x.height ?? 0) }));
      const subRows = (d.subOrders ?? [])
        .map((s: any) => ({ description: s.description ?? '', clientRefId: s.clientRefId ?? '', qty: Number(s.qty ?? 1) || 1 }))
        .filter((x: any) => x.description || x.clientRefId);
      const totalBoxes = dimRowsRaw.reduce((s, x) => s + (Number(x.qty) || 0), 0)
        || (d.subOrders ?? []).reduce((s, p) => s + (Number(p.qty) || 0), 0);
      // Per-box weight is no longer collected; the courier payload still expects one, so spread the
      // shipment's Total Weight evenly across all boxes.
      const perBoxWeight = totalBoxes > 0 && d.totalWeight ? Number((Number(d.totalWeight) / totalBoxes).toFixed(3)) : 0;
      const dimRows = dimRowsRaw.map(x => ({ ...x, weight: perBoxWeight }));
      const payload: any = {
        orderType: 'B2B',
        merchantId: Number(d.merchantId),
        courierId: d.courierId ? Number(d.courierId) : undefined,
        warehouseId: d.warehouseId ? Number(d.warehouseId) : undefined,
        lrNo: d.lrGenerateType === '2' ? d.lrNo : undefined,
        lrGenerateType: d.lrGenerateType,
        deliveryType: d.deliveryType,
        codValue: d.deliveryType === 'COD' ? Number(d.codValue ?? 0) : 0,
        insuranceType: d.insuranceType,
        freightCollection: d.freightCollection,
        pickupType: d.pickupType,
        customerName: d.customerName,
        customerMobile: d.customerMobile,
        customerAddress: d.customerAddress,
        customerCity: d.customerCity,
        customerState: d.customerState,
        customerPincode: d.customerPincode,
        totalBoxes,
        chargeableWeight: d.totalWeight,
        products: [{ name: d.productName, project: d.projectName, qty: totalBoxes, weight: d.totalWeight, isFragile: d.isFragile === 'Yes' }],
        dimensions: dimRows,   // physical boxes (box specs)
        subOrders: subRows,    // line items (independent of boxes)
        invoiceValue,
        userRemarks: JSON.stringify({ invoiceDetails: d.invoiceDetails }),
      };
      return (await api.post('/orders', payload)).data;
    },
    onSuccess: async (created: any) => {
      // If this order came from an Orders Bucket entry, mark it routed + link the order (best-effort —
      // the order is already created, so a link failure only leaves the bucket entry Pending).
      if (bucketId) {
        try {
          await api.post(`/orders-bucket/${bucketId}/link`, { orderId: String(created?.id ?? ''), as: 'B2B' });
          qc.invalidateQueries({ queryKey: ['orders-bucket'] });
        } catch (e: any) { toast.error('Order created, but updating the bucket failed: ' + (e?.response?.data?.message ?? 'unknown')); }
      }
      /*
       * Re-dispatch: this order IS the replacement for a returned one, so link the two now.
       *
       * Deliberately AFTER the create and best-effort: the replacement exists and is booked
       * whatever happens here, and failing the whole thing over a bookkeeping link would leave the
       * operator with a shipment they cannot see and no idea why. If it fails they are told, and
       * the RTO screen still offers "link by AWB" to finish the job by hand.
       */
      if (redispatchFrom) {
        const waybill = created?.awbNo ?? created?.lrNo ?? '';
        try {
          if (!waybill) throw new Error('the replacement has no waybill yet');
          await api.post(`/orders/rto/${redispatchFrom}/redispatch`, {
            awbNo: waybill,
            orderType: 'B2B',
            remarks: redispatchRemarks || 'Re-dispatched from RTO Orders',
          });
          qc.invalidateQueries({ queryKey: ['rto-list'] });
          qc.invalidateQueries({ queryKey: ['rto-summary'] });
          toast.success(`Linked to the returned shipment as ${waybill}`);
        } catch (e: any) {
          toast.error('Order created, but linking it to the RTO shipment failed: '
            + (e?.response?.data?.message ?? e?.message ?? 'unknown')
            + ' — open RTO Orders and use Re-dispatch → link by AWB.', { duration: 12000 });
        }
      }
      // Force an immediate refetch of the orders list AND tab counts (even while they're inactive/unmounted)
      // so the new order is already present when we land back on the list (default tab = Today Uploads).
      // A plain invalidate only marks inactive queries stale → the cached Today Uploads page could show
      // stale data on return; refetchType:'all' refetches them now.
      qc.invalidateQueries({
        predicate: (q: any) => Array.isArray(q.queryKey) && (q.queryKey[0] === 'orders' || q.queryKey[0] === 'order-tab-counts'),
        refetchType: 'all',
      });
      // A prepaid merchant over their limit still gets the ORDER saved, but it was not booked. Say so
      // loudly — a plain "created" would read as shipped when nothing was shipped.
      if (created?.bookingBlockedReason) {
        toast.warning(created.bookingBlockedReason, { duration: 12000 });
      } else {
        toast.success('B2B order created');
      }
      nav('/orders-b2b');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed'),
  });

  return (
    <div className="p-6 pb-28 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Create Order B2B</h1>
          {bucketId
            ? <p className="text-xs text-amber-700 mt-0.5">Pre-filled from Orders Bucket #{bucketId} — review, complete any missing fields, and Save. The bucket entry is marked routed once saved.</p>
            : dup && <p className="text-xs text-amber-700 mt-0.5">Duplicated from {dup.orderNo ?? dup.systemOrderNo ?? 'order'} — review &amp; edit before saving (AWB/LR not carried over).</p>}
        </div>
        <button onClick={() => nav(-1)} className="px-3 py-2 border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-50 flex items-center gap-2">
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <form onSubmit={handleSubmit((d) => {
        const errs = ruleCheck('B2B', normalizeB2b(d)).filter(i => i.severity === 'error');
        if (errs.length) { toast.error('Fix validation errors: ' + errs.map(e => e.message).join(' ')); return; }
        create.mutate(d);
      })} className="space-y-4">
        <Section title="Order Information">
          <div className="grid grid-cols-3 gap-3">
            <Field label="LR Generate Type *">
              <select {...register('lrGenerateType', { required: true })} className="input">
                <option value="1">Automatic</option>
                <option value="2">Manual</option>
              </select>
            </Field>
            {lrGenType === '2' && (
              <Field label="LR No *">
                <input {...register('lrNo', { required: 'Required when manual' })} className="input font-mono" />
              </Field>
            )}
            <Field label="Freight Collection Type *">
              <select {...register('freightCollection', { required: 'Required' })} className="input">
                <option value="">Select Type</option>
                <option value="BILL_TO_CLIENT">Bill to Client</option>
                <option value="COD">Collect on Delivery (COD)</option>
              </select>
            </Field>
            <Field label="Pickup Type *">
              <select {...register('pickupType', { required: 'Required' })} className="input">
                <option value="">Select Pickup Type</option>
                <option value="FM_PICKUP">FM-Pickup</option>
                <option value="SELF_DROP">Self-Drop</option>
              </select>
            </Field>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Insurance Type</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-1 text-sm"><input type="radio" value="OWNER_RISK" {...register('insuranceType')} /> OWNER RISK</label>
                <label className="flex items-center gap-1 text-sm"><input type="radio" value="CARRIER_RISK" {...register('insuranceType')} /> CARRIER RISK</label>
              </div>
            </div>
            {isMerchant ? (
              <input type="hidden" {...register('merchantId', { required: 'Required' })} />
            ) : (
              <Field label="Select Merchant *" error={errors.merchantId?.message}>
                <input type="hidden" {...register('merchantId', { required: 'Required' })} />
                <SearchableSelect value={watch('merchantId') ?? ''} onChange={(v) => setValue('merchantId', v as any, { shouldValidate: true })} options={(merchants ?? []).map(m => ({ value: m.id, label: m.companyName + ' (#' + m.id + ')' }))} placeholder="Select merchant" />
                <QuickCreateMerchant onCreated={(m) => setValue('merchantId', m.id, { shouldValidate: true })} />
              </Field>
            )}

            {/* Courier REQUIRED as of 2026-08-19, by request, to match the pickup location below.
                On B2B the field was already all but always filled — only 7 B2B orders in the
                preceding 30 days had no courier — so this codifies existing practice rather than
                changing it. (The B2C form's pickup rule is a genuine workflow change; see the note
                there before assuming the two are the same.) */}
            <Field label="Select Courier *" error={errors.courierId?.message}>
              <input type="hidden" {...register('courierId', { required: 'Choose a courier — a B2B consignment cannot be booked without one' })} />
              <SearchableSelect value={watch('courierId') ?? ''} onChange={(v) => setValue('courierId', v as any, { shouldValidate: true })} options={courierOptions.map((c: any) => ({ value: c.id, label: c.name + ' (#' + c.id + ')' }))} placeholder="Select Courier" />
              <QuickCreateCourier onCreated={(c) => setValue('courierId', c.id as any, { shouldValidate: true })} />
            </Field>
            {/* `error` is passed so the rule is VISIBLE. It was set without it, so a blank pickup
                location silently refused to submit and the form simply did nothing when Save was
                pressed — a validation nobody can see reads as a broken button. */}
            <Field label="Pickup Location *" error={errors.warehouseId?.message}>
              <input type="hidden" {...register('warehouseId', { required: 'Pickup location is required — the courier cannot book a shipment without one' })} />
              <SearchableSelect value={watch('warehouseId') ?? ''} onChange={(v) => setValue('warehouseId', v as any, { shouldValidate: true })} options={(warehouses ?? []).map(w => ({ value: w.id, label: w.name + ' (' + w.id + ')' }))} placeholder="Select pickup location" />
              <QuickCreatePickup merchantId={selectedMerchant} onCreated={(w) => setValue('warehouseId', w.id as any, { shouldValidate: true })} />
            </Field>
          </div>
        </Section>

        <Section title="Shipping Information">
          <div className="grid grid-cols-3 gap-3">
            <Field label="Customer Mobile *" error={errors.customerMobile?.message}>
              <input inputMode="numeric" maxLength={10} onKeyDown={onlyDigitKeys} {...register('customerMobile', {
                required: 'Required',
                setValueAs: (v: any) => String(v ?? '').replace(/\D/g, '').slice(-10),
                validate: (v: string) => /^[0-9]{10}$/.test(v) || 'Enter exactly 10 digits (e.g. 9876543210). +91 / spaces / dashes are stripped automatically.',
              })} className="input" />
            </Field>
            <Field label="Customer Name *" error={errors.customerName?.message}>
              <input {...register('customerName', { required: 'Required' })} className="input" />
            </Field>
            <Field label="Customer Address *" error={errors.customerAddress?.message}>
              <input {...register('customerAddress', { required: 'Required' })} className="input" />
            </Field>
            <Field label="Customer Pincode *" error={errors.customerPincode?.message}>
              <input maxLength={6} inputMode="numeric" onKeyDown={onlyDigitKeys} {...register('customerPincode', { required: 'Required', pattern: { value: /^\d{6}$/, message: '6 digits' }, setValueAs: (v: any) => String(v ?? '').replace(/\D/g, '').slice(0, 6) })} className="input font-mono" />
            </Field>
            <Field label="Customer City *" error={errors.customerCity?.message}>
              <input {...register('customerCity', { required: 'Required' })} className="input" />
            </Field>
            <Field label="Service State *" error={errors.customerState?.message}>
              <select {...register('customerState', { required: 'Required' })} className="input">
                <option value="">— Select state —</option>
                {states?.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </Field>
            {notServiceable && (
              <div className="col-span-3 text-sm rounded-md border border-rose-300 bg-rose-50 text-rose-700 px-3 py-2">
                ⛔ This pincode is <b>not serviceable</b> by the selected courier — you can&apos;t book this shipment. Choose a different courier or correct the pincode.
              </div>
            )}
            {!notServiceable && svcCheck.data?.oda && (
              <div className="col-span-3 text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
                ⚠ This pincode is <b>ODA (Out of Delivery Area)</b> for the selected courier — extra charges / longer delivery time may apply. You can still book.
              </div>
            )}
            <div className="col-span-3">
              <CustomerLookupCard
                merchantId={Number(selectedMerchant) || undefined}
                mobile={customerMobile}
                onPick={({ customer, address }) => {
                  setValue('customerName', customer.name);
                  setValue('customerAddress', address.address1);
                  setValue('customerPincode', address.pincode);
                  setValue('customerCity', address.city);
                  setValue('customerState', address.state);
                }}
              />
            </div>
          </div>
        </Section>

        <Section title="Product Information">
          <div className="grid grid-cols-4 gap-3">
            <Field label="Product Name"><input {...register('productName')} className="input" /></Field>
            <Field label="Project Name"><input {...register('projectName')} className="input" /></Field>
            <Field label="Total Weight In Kg(s)"><input type="number" step="0.001" min="0" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register('totalWeight', { valueAsNumber: true })} className="input" /></Field>
            <Field label="Is Fragile">
              <select {...register('isFragile')} className="input">
                <option value="">Select</option>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </Field>
          </div>
        </Section>

        <Section title="Sub Orders">
          {subOrders.map((s, i) => (
            <div key={s.id} className="grid grid-cols-4 gap-2 items-end">
              <Field label="Description"><input {...register(`subOrders.${i}.description` as const)} className="input" /></Field>
              <Field label="Client reference ID / Order ID"><input {...register(`subOrders.${i}.clientRefId` as const)} className="input" /></Field>
              <Field label="Quantity"><input type="number" min="1" step="1" inputMode="numeric" onKeyDown={onlyDigitKeys} {...register(`subOrders.${i}.qty` as const, { valueAsNumber: true, min: { value: 1, message: 'Must be > 0' } })} className="input" /></Field>
              {subOrders.length > 1 && <button type="button" onClick={() => rmSub(i)} className="text-danger text-xs px-2 self-center">× Remove</button>}
            </div>
          ))}
          <AddMoreButton onClick={() => addSub({})} />
        </Section>

        <Section title="Dimensions Details in CM[s]">
          {/* Remove sits in its own narrow track (not an equal 5th column) so the four numeric
              fields keep their full width and a 3-digit dimension fits without clipping. */}
          {dims.map((d, i) => (
            <div key={d.id} className="grid grid-cols-[repeat(4,minmax(0,1fr))_auto] gap-2 items-end">
              <Field label="Qty"><input type="number" min="1" step="1" inputMode="numeric" onKeyDown={onlyDigitKeys} {...register(`dimensions.${i}.qty` as const, { valueAsNumber: true, min: { value: 1, message: 'Must be > 0' } })} className="input input-num" /></Field>
              <Field label="Length (cm)"><input type="number" min="0.1" step="0.1" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register(`dimensions.${i}.length` as const, { valueAsNumber: true, min: { value: 0.1, message: 'Must be > 0' } })} className="input input-num" /></Field>
              <Field label="Breadth (cm)"><input type="number" min="0.1" step="0.1" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register(`dimensions.${i}.breadth` as const, { valueAsNumber: true, min: { value: 0.1, message: 'Must be > 0' } })} className="input input-num" /></Field>
              <Field label="Height (cm)"><input type="number" min="0.1" step="0.1" inputMode="decimal" onKeyDown={blockNonNumericKeys} {...register(`dimensions.${i}.height` as const, { valueAsNumber: true, min: { value: 0.1, message: 'Must be > 0' } })} className="input input-num" /></Field>
              {dims.length > 1 && <button type="button" onClick={() => rmDim(i)} className="text-danger text-xs px-2 self-center">× Remove</button>}
            </div>
          ))}
          <AddMoreButton onClick={() => addDim({})} />
        </Section>

        <Section title="Invoice Details">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Delivery Type *">
              <select {...register('deliveryType', { required: true })} className="input">
                <option value="COD">COD</option>
                <option value="PREPAID">Prepaid</option>
              </select>
            </Field>
            {watch('deliveryType') === 'COD' && (
              <Field label="COD Amount *" error={errors.codValue?.message}>
                <input type="number" step="0.01" min={1} inputMode="decimal"
                  onKeyDown={(e) => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }}
                  onPaste={(e) => { const t = e.clipboardData.getData('text'); if (!/^\d*\.?\d*$/.test(t)) e.preventDefault(); }}
                  {...register('codValue', { required: 'COD Amount is required for COD orders', valueAsNumber: true, min: { value: 1, message: 'Must be greater than 0' } })}
                  className="input" placeholder="Enter COD amount" />
              </Field>
            )}
          </div>
          {invs.map((iv, i) => (
            <div key={iv.id} className="grid grid-cols-4 gap-2 items-end">
              <Field label="Product Invoice Value"><input type="number" step="0.01" min={0} inputMode="decimal" onKeyDown={(e) => { if (['e','E','+','-'].includes(e.key)) e.preventDefault(); }} onPaste={(e) => { const t = e.clipboardData.getData('text'); if (!/^\d*\.?\d*$/.test(t)) e.preventDefault(); }} {...register(`invoiceDetails.${i}.productInvoiceValue` as const, { valueAsNumber: true, min: { value: 0, message: 'Must be a positive number' } })} className="input" /></Field>
              <Field label="Invoice Number"><input {...register(`invoiceDetails.${i}.invoiceNumber` as const)} className="input" /></Field>
              <Field label="E-way Bill Number"><input {...register(`invoiceDetails.${i}.ewayBillNumber` as const)} className="input" /></Field>
              {invs.length > 1 && <button type="button" onClick={() => rmInv(i)} className="text-danger text-xs px-2 self-center">× Remove</button>}
            </div>
          ))}
          <AddMoreButton onClick={() => addInv({})} />
        </Section>

        <OrderValidationPanel orderType="B2B" fields={normalizeB2b(watch())} />

        <div className="flex justify-end gap-2">
          <button type="button" onClick={() => nav(-1)} className="px-4 py-2 border border-red-300 text-danger text-sm rounded-lg hover:bg-red-50">CANCEL</button>
          <button type="submit" disabled={create.isPending}
            className="px-4 py-2 bg-emerald-700 text-white text-sm rounded-lg font-medium hover:bg-emerald-800 transition disabled:opacity-60 flex items-center gap-2">
            {create.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

// Centered "Add More" button, rendered right after the last row of a repeatable section so it
// sits in the natural tab order — pressing Tab from the last field of the last row lands on it,
// letting the user add rows without reaching for the mouse.
function AddMoreButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex justify-center pt-2">
      <button type="button" onClick={onClick}
        className="text-emerald-700 text-xs border border-emerald-300 px-4 py-1.5 rounded hover:bg-emerald-50 flex items-center gap-1">
        <Plus size={12} /> Add More
      </button>
    </div>
  );
}

function Section({ title, children, right }: { title: string; children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  const required = label.trim().endsWith('*');
  const base = required ? label.replace(/\s*\*\s*$/, '') : label;
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{base}{required && <span className="text-rose-500 ml-0.5">*</span>}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
